import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import api, { groupAPI, learningAPI, virtualMedicAPI } from '../../services/api';

const formatDate = (value, includeTime = false) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return date.toLocaleString(undefined, includeTime
    ? { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    : { day: 'numeric', month: 'short', year: 'numeric' });
};

const scoreLabel = (value) => (Number.isFinite(Number(value)) ? `${Math.round(Number(value) * 10) / 10}%` : 'No attempts');

const metricCard = (label, value, helper, icon) => (
  <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {label}
          </Typography>
          <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 900 }}>{value}</Typography>
          {helper && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{helper}</Typography>}
        </Box>
        <Box sx={{ color: 'primary.main' }}>{icon}</Box>
      </Stack>
    </CardContent>
  </Card>
);

export function TeachingOverview({ students, groups, assignments, announcements, onSelectTab }) {
  const attempts = students.reduce((sum, student) => sum + (student.performance?.attempts || 0), 0);
  const scoredStudents = students.filter((student) => Number.isFinite(Number(student.performance?.averageScore)));
  const average = scoredStudents.length
    ? scoredStudents.reduce((sum, student) => sum + Number(student.performance.averageScore), 0) / scoredStudents.length
    : null;
  const activeAssignments = assignments.filter((assignment) => assignment.status === 'active');
  const overdueAssignments = activeAssignments.filter((assignment) => assignment.dueAt && new Date(assignment.dueAt) < new Date());
  const recentAnnouncements = announcements.filter((announcement) => announcement.status === 'active').slice(0, 3);

  return (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} lg={3}>{metricCard('Learners', students.length, `${groups.length} active cohorts`, <PersonSearchRoundedIcon />)}</Grid>
        <Grid item xs={12} sm={6} lg={3}>{metricCard('Completed attempts', attempts, 'Across active learners', <InsightsRoundedIcon />)}</Grid>
        <Grid item xs={12} sm={6} lg={3}>{metricCard('Average score', average === null ? '—' : `${Math.round(average * 10) / 10}%`, 'Learners with recorded attempts', <InsightsRoundedIcon />)}</Grid>
        <Grid item xs={12} sm={6} lg={3}>{metricCard('Active assignments', activeAssignments.length, overdueAssignments.length ? `${overdueAssignments.length} past deadline` : 'No overdue work', <ChecklistRoundedIcon />)}</Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, height: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>Cohort pulse</Typography>
                <Typography variant="body2" color="text.secondary">Where tutor attention may be useful.</Typography>
              </Box>
              <Button size="small" endIcon={<ArrowForwardRoundedIcon />} onClick={() => onSelectTab(2)}>All cohorts</Button>
            </Stack>
            {groups.length ? (
              <Stack spacing={1.25}>
                {groups.slice(0, 5).map((group) => (
                  <Stack key={group.id} direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>{group.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{group.academicYear || group.cohortCode || 'Cohort'}</Typography>
                    </Box>
                    <Chip label={`${group.memberCount ?? group.memberIds?.length ?? 0} learners`} size="small" />
                    <Button component={RouterLink} to={`/university/teaching/cohorts/${group.id}`} size="small">Open</Button>
                  </Stack>
                ))}
              </Stack>
            ) : <Alert severity="info">Create a cohort to organise learners and assign cases in one action.</Alert>}
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, height: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>Recent announcements</Typography>
                <Typography variant="body2" color="text.secondary">Latest messages sent to learners.</Typography>
              </Box>
              <Button size="small" onClick={() => onSelectTab(4)}>Manage</Button>
            </Stack>
            {recentAnnouncements.length ? (
              <Stack spacing={1.5} divider={<Divider flexItem />}>
                {recentAnnouncements.map((announcement) => (
                  <Box key={announcement.id}>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>{announcement.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {announcement.audienceType === 'all' ? 'Everyone' : announcement.groups?.map((group) => group.name).join(', ') || 'Selected learners'} · {formatDate(announcement.publishAt)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            ) : <Typography variant="body2" color="text.secondary">No announcements have been sent.</Typography>}
          </Paper>
        </Grid>
      </Grid>
    </Stack>
  );
}

export function TeachingStudents({ students }) {
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const groups = useMemo(() => {
    const byId = new Map();
    students.forEach((student) => (student.groups || []).forEach((group) => byId.set(group.id, group)));
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [students]);
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return students.filter((student) => {
      const matchesSearch = !term || `${student.name} ${student.email}`.toLowerCase().includes(term);
      const matchesGroup = groupFilter === 'all' || student.groups?.some((group) => group.id === groupFilter);
      return matchesSearch && matchesGroup;
    });
  }, [groupFilter, search, students]);

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
        <TextField
          fullWidth
          label="Search learners"
          placeholder="Name or email"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel id="student-cohort-filter">Cohort</InputLabel>
          <Select labelId="student-cohort-filter" label="Cohort" value={groupFilter} onChange={(event) => setGroupFilter(event.target.value)}>
            <MenuItem value="all">All cohorts</MenuItem>
            {groups.map((group) => <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Learner</TableCell>
                <TableCell>Cohorts</TableCell>
                <TableCell align="right">Attempts</TableCell>
                <TableCell align="right">Average</TableCell>
                <TableCell>Last attempt</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((student) => (
                <TableRow key={student.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>{student.name || 'Unnamed learner'}</Typography>
                    <Typography variant="caption" color="text.secondary">{student.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {(student.groups || []).map((group) => <Chip key={group.id} size="small" label={group.name} />)}
                      {!student.groups?.length && <Typography variant="body2" color="text.secondary">Unassigned</Typography>}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{student.performance?.attempts || 0}</TableCell>
                  <TableCell align="right">{scoreLabel(student.performance?.averageScore)}</TableCell>
                  <TableCell>{formatDate(student.performance?.lastAttemptAt)}</TableCell>
                  <TableCell align="right">
                    <Button component={RouterLink} to={`/university/teaching/students/${student.id}`} endIcon={<ArrowForwardRoundedIcon />}>
                      View insights
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}>No learners match these filters.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Stack>
  );
}

const emptyGroup = { name: '', description: '', cohortCode: '', academicYear: '', tags: '' };

export function TeachingCohorts({ groups, onRefresh }) {
  const [dialog, setDialog] = useState({ open: false, group: null });
  const [form, setForm] = useState(emptyGroup);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const openDialog = (group = null) => {
    setDialog({ open: true, group });
    setForm(group ? {
      name: group.name || '',
      description: group.description || '',
      cohortCode: group.cohortCode || '',
      academicYear: group.academicYear || '',
      tags: (group.tags || []).join(', '),
    } : emptyGroup);
    setError('');
  };

  const saveGroup = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        tags: form.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
      };
      if (dialog.group) await groupAPI.update(dialog.group.id, payload);
      else await groupAPI.create(payload);
      setDialog({ open: false, group: null });
      await onRefresh();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to save this cohort.');
    } finally {
      setSaving(false);
    }
  };

  const archiveGroup = async (group) => {
    if (!window.confirm(`Archive ${group.name}? Learners and their histories will remain in the university.`)) return;
    try {
      await groupAPI.archive(group.id);
      setError('');
      await onRefresh();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to archive this cohort.');
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>Cohorts and groups</Typography>
          <Typography variant="body2" color="text.secondary">Open a cohort to see its learners, activity, and current assignments.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => openDialog()}>New cohort</Button>
      </Stack>
      {error && !dialog.open && <Alert severity="error">{error}</Alert>}
      <Grid container spacing={2}>
        {groups.map((group) => (
          <Grid item xs={12} md={6} lg={4} key={group.id}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 3, display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flex: 1 }}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>{group.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{group.cohortCode || 'Custom cohort'}{group.academicYear ? ` · ${group.academicYear}` : ''}</Typography>
                  </Box>
                  <Chip icon={<GroupsRoundedIcon />} label={group.memberCount ?? group.memberIds?.length ?? 0} size="small" />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, minHeight: 40 }}>
                  {group.description || 'No cohort description has been added.'}
                </Typography>
                {!!group.tags?.length && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                    {group.tags.slice(0, 4).map((tag) => <Chip key={tag} size="small" label={tag} variant="outlined" />)}
                  </Stack>
                )}
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2 }}>
                <Button component={RouterLink} to={`/university/teaching/cohorts/${group.id}`} variant="contained" size="small">
                  Open cohort
                </Button>
                <Button size="small" onClick={() => openDialog(group)}>Edit details</Button>
                <Button size="small" color="error" onClick={() => archiveGroup(group)}>Archive</Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
        {!groups.length && (
          <Grid item xs={12}><Alert severity="info">No cohorts yet. Create one to organise learners, assignments, and announcements.</Alert></Grid>
        )}
      </Grid>

      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, group: null })} fullWidth maxWidth="sm">
        <DialogTitle>{dialog.group ? 'Edit cohort' : 'Create cohort'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField required label="Cohort name" value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} />
            <TextField label="Description" multiline minRows={2} value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth label="Cohort code" value={form.cohortCode} onChange={(event) => setForm((current) => ({ ...current, cohortCode: event.target.value }))} />
              <TextField fullWidth label="Academic year" placeholder="2026/27" value={form.academicYear} onChange={(event) => setForm((current) => ({ ...current, academicYear: event.target.value }))} />
            </Stack>
            <TextField label="Tags" helperText="Optional, comma separated" value={form.tags} onChange={(event) => setForm((current) => ({ ...current, tags: event.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false, group: null })}>Cancel</Button>
          <Button variant="contained" onClick={saveGroup} disabled={saving || !form.name.trim()}>{saving ? 'Saving…' : 'Save cohort'}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

const normalizeCase = (caseData, source) => ({
  id: caseData.id || caseData._id,
  title: caseData.title || 'Untitled case',
  specialty: caseData.specialty || '',
  presentingComplaint: caseData.presentingComplaint || '',
  source,
});

export function TeachingAssignments({ assignments, groups, students, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cases, setCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [assignmentView, setAssignmentView] = useState('active');
  const [form, setForm] = useState({ selectedCase: null, groups: [], students: [], availableAt: '', dueAt: '', maxAttempts: 1, instructions: '' });
  const activeAssignments = assignments.filter((assignment) => assignment.status === 'active');
  const archivedAssignments = assignments.filter((assignment) => assignment.status === 'archived');
  const visibleAssignments = assignmentView === 'archived' ? archivedAssignments : activeAssignments;

  useEffect(() => {
    if (!dialogOpen || cases.length) return;
    let active = true;
    const loadCases = async () => {
      setCasesLoading(true);
      const [tenantResult, officialResult] = await Promise.allSettled([
        api.get('/cases', { params: { library: 'tenant', environment: 'production', limit: 1000, sortBy: 'title', sortOrder: 'asc' } }),
        virtualMedicAPI.listLibraryCases({ limit: 1000 }),
      ]);
      if (!active) return;
      const rows = [
        ...(tenantResult.status === 'fulfilled' ? (tenantResult.value.data?.cases || []).map((item) => normalizeCase(item, 'University')) : []),
        ...(officialResult.status === 'fulfilled' ? (officialResult.value.data?.cases || []).map((item) => normalizeCase(item, 'Virtual Medic')) : []),
      ];
      const unique = new Map(rows.filter((item) => item.id).map((item) => [item.id.toString(), item]));
      setCases(Array.from(unique.values()).sort((a, b) => a.title.localeCompare(b.title)));
      if (!rows.length) setError('No published cases are currently available to assign.');
      setCasesLoading(false);
    };
    loadCases();
    return () => { active = false; };
  }, [cases.length, dialogOpen]);

  const createAssignment = async () => {
    if (!form.selectedCase || (!form.groups.length && !form.students.length)) return;
    setSaving(true);
    try {
      await learningAPI.createAssignment({
        caseId: form.selectedCase.id,
        groupIds: form.groups.map((group) => group.id),
        userIds: form.students.map((student) => student.id),
        availableAt: form.availableAt ? new Date(form.availableAt).toISOString() : undefined,
        dueAt: form.dueAt ? new Date(form.dueAt).toISOString() : undefined,
        maxAttempts: Number(form.maxAttempts) || 1,
        instructions: form.instructions.trim(),
      });
      setDialogOpen(false);
      setForm({ selectedCase: null, groups: [], students: [], availableAt: '', dueAt: '', maxAttempts: 1, instructions: '' });
      await onRefresh();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to create this assignment.');
    } finally {
      setSaving(false);
    }
  };

  const archiveAssignment = async (assignmentId) => {
    if (!window.confirm('Archive this assignment? Learners will no longer see it as active.')) return;
    try {
      await learningAPI.archiveAssignment(assignmentId);
      await onRefresh();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to archive this assignment.');
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>Assigned practice</Typography>
          <Typography variant="body2" color="text.secondary">Set one case for cohorts or selected learners, with a deadline and attempt limit.</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ButtonGroup size="small" aria-label="Assignment view">
            <Button variant={assignmentView === 'active' ? 'contained' : 'outlined'} onClick={() => setAssignmentView('active')}>
              Active ({activeAssignments.length})
            </Button>
            <Button variant={assignmentView === 'archived' ? 'contained' : 'outlined'} onClick={() => setAssignmentView('archived')}>
              Archived ({archivedAssignments.length})
            </Button>
          </ButtonGroup>
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => { setError(''); setDialogOpen(true); }}>Assign case</Button>
        </Stack>
      </Stack>
      {error && !dialogOpen && <Alert severity="error">{error}</Alert>}
      <Stack spacing={1.5}>
        {visibleAssignments.map((assignment) => {
          const total = assignment.progress?.assignedStudents || 0;
          const complete = assignment.progress?.studentsCompleted || 0;
          const percent = total ? (complete / total) * 100 : 0;
          return (
            <Paper key={assignment.id} variant="outlined" sx={{ p: 2.25, borderRadius: 3 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 900 }}>{assignment.caseTitle}</Typography>
                    {assignment.status === 'archived' && <Chip size="small" variant="outlined" label="Archived · read only" />}
                    {assignment.dueAt && new Date(assignment.dueAt) < new Date() && <Chip size="small" color="warning" label="Past deadline" />}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {assignment.groups?.map((group) => group.name).join(', ') || `${assignment.userIds?.length || 0} selected learners`} · {assignment.maxAttempts} attempt{assignment.maxAttempts === 1 ? '' : 's'} · Due {formatDate(assignment.dueAt, true)}
                  </Typography>
                  {assignment.instructions && <Typography variant="body2" sx={{ mt: 1.25 }}>{assignment.instructions}</Typography>}
                  <Box sx={{ mt: 1.75, maxWidth: 520 }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary">{complete} completed · {assignment.progress?.studentsStarted || 0} started</Typography>
                      <Typography variant="caption" color="text.secondary">{total} assigned</Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={percent} sx={{ mt: 0.6, height: 7, borderRadius: 99 }} />
                  </Box>
                </Box>
                {assignment.status === 'active' && (
                  <Button color="error" size="small" onClick={() => archiveAssignment(assignment.id)} sx={{ alignSelf: { md: 'flex-start' } }}>
                    Archive
                  </Button>
                )}
              </Stack>
            </Paper>
          );
        })}
        {!visibleAssignments.length && (
          <Alert severity="info">
            {assignmentView === 'active' ? 'There are no active assignments.' : 'There is no archived assignment history.'}
          </Alert>
        )}
      </Stack>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Assign a case</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <Autocomplete
              options={cases}
              loading={casesLoading}
              value={form.selectedCase}
              onChange={(_event, selectedCase) => setForm((current) => ({ ...current, selectedCase }))}
              getOptionLabel={(option) => `${option.title}${option.specialty ? ` · ${option.specialty}` : ''}`}
              groupBy={(option) => option.source}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => <TextField {...params} required label="Case" helperText="Published university and Virtual Medic cases" />}
            />
            <Autocomplete
              multiple
              options={groups}
              value={form.groups}
              onChange={(_event, selectedGroups) => setForm((current) => ({ ...current, groups: selectedGroups }))}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => <TextField {...params} label="Cohorts" helperText="Choose cohorts, individual learners, or both" />}
            />
            <Autocomplete
              multiple
              options={students}
              value={form.students}
              onChange={(_event, selectedStudents) => setForm((current) => ({ ...current, students: selectedStudents }))}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => <TextField {...params} label="Individual learners" />}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Available from"
                InputLabelProps={{ shrink: true }}
                value={form.availableAt}
                onChange={(event) => setForm((current) => ({ ...current, availableAt: event.target.value }))}
                helperText="Leave blank for immediately"
              />
              <TextField
                fullWidth
                type="datetime-local"
                label="Deadline"
                InputLabelProps={{ shrink: true }}
                value={form.dueAt}
                onChange={(event) => setForm((current) => ({ ...current, dueAt: event.target.value }))}
                helperText="Optional"
              />
              <TextField
                sx={{ minWidth: 150 }}
                type="number"
                label="Max attempts"
                value={form.maxAttempts}
                onChange={(event) => setForm((current) => ({ ...current, maxAttempts: event.target.value }))}
                inputProps={{ min: 1, max: 100 }}
              />
            </Stack>
            <TextField multiline minRows={3} label="Instructions" inputProps={{ maxLength: 2000 }} value={form.instructions} onChange={(event) => setForm((current) => ({ ...current, instructions: event.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={createAssignment}
            disabled={saving || !form.selectedCase || (!form.groups.length && !form.students.length)}
          >
            {saving ? 'Assigning…' : 'Assign case'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

export function TeachingAnnouncements({ announcements, groups, students, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ title: '', body: '', audienceType: 'all', groups: [], students: [], expiresAt: '' });

  const publish = async () => {
    setSaving(true);
    try {
      await learningAPI.createAnnouncement({
        title: form.title.trim(),
        body: form.body.trim(),
        audienceType: form.audienceType,
        groupIds: form.audienceType === 'groups' ? form.groups.map((group) => group.id) : [],
        userIds: form.audienceType === 'users' ? form.students.map((student) => student.id) : [],
        expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : undefined,
      });
      setDialogOpen(false);
      setForm({ title: '', body: '', audienceType: 'all', groups: [], students: [], expiresAt: '' });
      await onRefresh();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to publish this announcement.');
    } finally {
      setSaving(false);
    }
  };

  const archive = async (announcementId) => {
    try {
      await learningAPI.archiveAnnouncement(announcementId);
      await onRefresh();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to archive this announcement.');
    }
  };

  const audienceReady = form.audienceType === 'all'
    || (form.audienceType === 'groups' && form.groups.length)
    || (form.audienceType === 'users' && form.students.length);

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>Announcements</Typography>
          <Typography variant="body2" color="text.secondary">Send a concise notification to everyone, cohorts, or selected learners.</Typography>
        </Box>
        <Button variant="contained" startIcon={<CampaignRoundedIcon />} onClick={() => { setError(''); setDialogOpen(true); }}>New announcement</Button>
      </Stack>
      {error && !dialogOpen && <Alert severity="error">{error}</Alert>}
      <Stack spacing={1.25}>
        {announcements.map((announcement) => (
          <Paper key={announcement.id} variant="outlined" sx={{ p: 2.25, borderRadius: 3, opacity: announcement.status === 'archived' ? 0.62 : 1 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
              <Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                  <Typography variant="h6" sx={{ fontWeight: 900 }}>{announcement.title}</Typography>
                  <Chip size="small" label={announcement.status} color={announcement.status === 'active' ? 'success' : 'default'} />
                </Stack>
                <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{announcement.body}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
                  {announcement.audienceType === 'all' ? 'All university members' : announcement.groups?.map((group) => group.name).join(', ') || 'Selected learners'} · Sent {formatDate(announcement.publishAt, true)}{announcement.createdBy ? ` by ${announcement.createdBy}` : ''}
                </Typography>
              </Box>
              {announcement.status !== 'archived' && <Button color="error" size="small" onClick={() => archive(announcement.id)} sx={{ alignSelf: { md: 'flex-start' } }}>Archive</Button>}
            </Stack>
          </Paper>
        ))}
        {!announcements.length && <Alert severity="info">No announcements have been published.</Alert>}
      </Stack>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Publish announcement</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField required label="Title" inputProps={{ maxLength: 180 }} value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} />
            <TextField required multiline minRows={4} label="Message" inputProps={{ maxLength: 4000 }} value={form.body} onChange={(event) => setForm((current) => ({ ...current, body: event.target.value }))} />
            <FormControl>
              <InputLabel id="announcement-audience">Audience</InputLabel>
              <Select labelId="announcement-audience" label="Audience" value={form.audienceType} onChange={(event) => setForm((current) => ({ ...current, audienceType: event.target.value }))}>
                <MenuItem value="all">Everyone at the university</MenuItem>
                <MenuItem value="groups">Specific cohorts</MenuItem>
                <MenuItem value="users">Specific learners</MenuItem>
              </Select>
            </FormControl>
            {form.audienceType === 'groups' && (
              <Autocomplete
                multiple options={groups} value={form.groups}
                onChange={(_event, selectedGroups) => setForm((current) => ({ ...current, groups: selectedGroups }))}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => <TextField {...params} label="Cohorts" />}
              />
            )}
            {form.audienceType === 'users' && (
              <Autocomplete
                multiple options={students} value={form.students}
                onChange={(_event, selectedStudents) => setForm((current) => ({ ...current, students: selectedStudents }))}
                getOptionLabel={(option) => `${option.name} (${option.email})`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => <TextField {...params} label="Learners" />}
              />
            )}
            <TextField
              type="datetime-local" label="Expires" InputLabelProps={{ shrink: true }} helperText="Optional"
              value={form.expiresAt} onChange={(event) => setForm((current) => ({ ...current, expiresAt: event.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={publish} disabled={saving || !form.title.trim() || !form.body.trim() || !audienceReady}>
            {saving ? 'Publishing…' : 'Publish now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
